import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "../../utils/toast";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Box from "@material-ui/core/Box";
import WarningIcon from "@material-ui/icons/Warning";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const validateWhatsAppNumber = (value, context) => {
	if (!value) return true;
	const normalized = normalizeContactNumber(value);
	if (normalized.length < 13) {
		return new Yup.ValidationError(
			i18n.t("contactModal.form.numberErrorTooShort")
		);
	}
	if (!normalized.startsWith("55")) {
		return new Yup.ValidationError(
			i18n.t("contactModal.form.numberErrorNoBrazilDDI")
		);
	}
	return true;
};

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, i18n.t("validation.nameTooShort"))
		.max(50, i18n.t("validation.nameTooLong"))
		.required(i18n.t("validation.nameRequired")),
	number: Yup.string()
		.required(i18n.t("validation.numberRequired"))
		.test("valid-whatsapp", i18n.t("contactModal.form.numberErrorInvalid"), validateWhatsAppNumber),
	email: Yup.string().email(i18n.t("validation.emailInvalid")),
});

const normalizeContactNumber = value => {
	let normalized = (value || "").toString().replace(/\D/g, "");

	if (normalized.startsWith("00")) {
		normalized = normalized.substring(2);
	}

	normalized = normalized.replace(/^0+/, "");

	if (normalized && !normalized.startsWith("55")) {
		normalized = `55${normalized}`;
	}

	return normalized;
};

const formatContactNumber = value => {
	const digits = normalizeContactNumber(value);

	if (!digits) return "";

	const country = digits.slice(0, 2);
	const area = digits.slice(2, 4);
	const subscriber = digits.slice(4, 13);

	if (!area) return `+${country}`;
	if (subscriber.length <= 4) return `+${country} (${area}) ${subscriber}`;
	if (subscriber.length <= 8) {
		return `+${country} (${area}) ${subscriber.slice(0, 4)}-${subscriber.slice(4)}`;
	}

	return `+${country} (${area}) ${subscriber.slice(0, 5)}-${subscriber.slice(5, 9)}`;
};

const mergeExtraInfoTemplates = (currentExtraInfo = [], selectedTemplates = []) => {
	const existingKeys = new Set(
		currentExtraInfo.map(info => `${(info.name || "").trim()}::${(info.value || "").trim()}`)
	);

	const merged = [...currentExtraInfo];

	selectedTemplates.forEach(template => {
		const name = (template?.name || "").trim();
		const value = (template?.value || "").trim();

		if (!name) return;

		const key = `${name}::${value}`;
		if (!existingKeys.has(key)) {
			merged.push({ name, value });
			existingKeys.add(key);
		}
	});

	return merged;
};

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		address: "",
		birthDate: "",
		disableBot: false
	};

	const [contact, setContact] = useState(initialState);
	const [extraInfoTemplates, setExtraInfoTemplates] = useState([]);
	const [selectedTemplateOptions, setSelectedTemplateOptions] = useState([]);
	const [conflictData, setConflictData] = useState(null);
	const [pendingPayload, setPendingPayload] = useState(null);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				const formattedInitialValues = { ...initialValues };
				if (formattedInitialValues.birthDate) {
					formattedInitialValues.birthDate = formattedInitialValues.birthDate.split("T")[0];
				}
				formattedInitialValues.number = formatContactNumber(formattedInitialValues.number);
				setContact(prevState => {
					return { ...prevState, ...formattedInitialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					if (data.birthDate) {
						data.birthDate = data.birthDate.split("T")[0];
					}
					data.number = formatContactNumber(data.number);
					setContact(data);
				}
			} catch (err) {
				toastError(err);
			}
		};

		const fetchExtraInfoTemplates = async () => {
			if (!open) return;

			try {
				const { data } = await api.get("/contacts/extra-info/templates");
				if (isMounted.current) {
					setExtraInfoTemplates(data.templates || []);
				}
			} catch (err) {
				toastError(err);
			}
		};

		setSelectedTemplateOptions([]);
		fetchContact();
		fetchExtraInfoTemplates();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setConflictData(null);
		setPendingPayload(null);
	};

	const handleSaveContact = async values => {
		const payload = {
			...values,
			number: normalizeContactNumber(values.number),
			birthDate: values.birthDate ? values.birthDate : null,
			extraInfo: (values.extraInfo || []).filter(
				info => (info.name || "").trim() || (info.value || "").trim()
			)
		};

		try {
			if (contactId) {
				await api.put(`/contacts/${contactId}`, payload);
				handleClose();
				toast.success(i18n.t("contactModal.success"));
			} else {
				const { data } = await api.post("/contacts", payload);
				if (data.alreadyExists) {
					setPendingPayload(payload);
					setConflictData(data.existingContact);
					return;
				}
				if (onSave) {
					onSave(data);
				}
				handleClose();
				toast.success(i18n.t("contactModal.success"));
			}
		} catch (err) {
			toastError(err);
		}
	};

	const handleConflictOverwrite = async () => {
		try {
			const { data } = await api.put(`/contacts/${conflictData.id}`, pendingPayload);
			if (onSave) onSave(data);
			handleClose();
			toast.success("Contato atualizado com os novos dados.");
		} catch (err) {
			toastError(err);
		}
	};

	const handleConflictMerge = async () => {
		try {
			const merged = { ...pendingPayload };
			if (conflictData.name && !merged.name) merged.name = conflictData.name;
			if (conflictData.email && !merged.email) merged.email = conflictData.email;
			if (conflictData.address && !merged.address) merged.address = conflictData.address;
			if (conflictData.birthDate && !merged.birthDate) merged.birthDate = conflictData.birthDate;
			// Merge extra info
			const existingExtra = (conflictData.extraInfo || []).map(e => ({ name: e.name, value: e.value }));
			const newExtra = merged.extraInfo || [];
			const existingKeys = new Set(existingExtra.map(e => `${e.name}::${e.value}`));
			newExtra.forEach(e => {
				const key = `${e.name}::${e.value}`;
				if (!existingKeys.has(key)) {
					existingExtra.push(e);
					existingKeys.add(key);
				}
			});
			merged.extraInfo = existingExtra;

			const { data } = await api.put(`/contacts/${conflictData.id}`, merged);
			if (onSave) onSave(data);
			handleClose();
			toast.success("Dados mesclados com sucesso. Campos vazios foram preenchidos.");
		} catch (err) {
			toastError(err);
		}
	};

	if (conflictData && pendingPayload) {
		const fields = [
			{ label: "Nome", key: "name" },
			{ label: "Email", key: "email" },
			{ label: "Endereço", key: "address" },
			{ label: "Data de Nascimento", key: "birthDate" },
		];
		return (
			<div className={classes.root}>
				<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
					<DialogTitle>
						<Box display="flex" alignItems="center" style={{ gap: 8 }}>
							<WarningIcon style={{ color: "#ff9800" }} />
							Contato já existente
						</Box>
					</DialogTitle>
					<DialogContent dividers>
						<Typography variant="body2" style={{ marginBottom: 16 }}>
							O número <strong>{formatContactNumber(conflictData.number)}</strong> já está cadastrado
							no nome de <strong>{conflictData.name}</strong>. Escolha como deseja prosseguir:
						</Typography>
						<Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
							<Box style={{ padding: 12, borderRadius: 8, backgroundColor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
								<Typography variant="subtitle2" style={{ marginBottom: 8, color: "#aaa" }}>Dados existentes</Typography>
								{fields.map(f => (
									<Typography key={f.key} variant="body2" style={{ marginBottom: 4 }}>
										<strong>{f.label}:</strong> {conflictData[f.key] || <em style={{ color: "#666" }}>vazio</em>}
									</Typography>
								))}
							</Box>
							<Box style={{ padding: 12, borderRadius: 8, backgroundColor: "rgba(255,53,0,0.05)", border: "1px solid rgba(255,53,0,0.15)" }}>
								<Typography variant="subtitle2" style={{ marginBottom: 8, color: "rgba(255,53,0,0.8)" }}>Novos dados</Typography>
								{fields.map(f => (
									<Typography key={f.key} variant="body2" style={{ marginBottom: 4 }}>
										<strong>{f.label}:</strong> {pendingPayload[f.key] || <em style={{ color: "#666" }}>vazio</em>}
									</Typography>
								))}
							</Box>
						</Box>
						<Typography variant="caption" color="textSecondary">
							<strong>Substituir:</strong> sobrescreve todos os dados do contato existente pelos novos dados.
							<br />
							<strong>Mesclar:</strong> preenche apenas os campos que estão vazios no contato existente, mantendo os dados atuais.
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose} color="secondary" variant="outlined">
							Cancelar
						</Button>
						<Button onClick={handleConflictMerge} style={{ backgroundColor: "#2e7d32", color: "#fff" }} variant="contained">
							Mesclar
						</Button>
						<Button onClick={handleConflictOverwrite} style={{ backgroundColor: "rgba(255,53,0,0.8)", color: "#fff" }} variant="contained">
							Substituir
						</Button>
					</DialogActions>
				</Dialog>
			</div>
		);
	}

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<TextField
									label={i18n.t("contactModal.form.number")}
									name="number"
									value={values.number || ""}
									onChange={event => {
										setFieldValue("number", formatContactNumber(event.target.value));
									}}
									onBlur={event => {
										setFieldValue("number", formatContactNumber(event.target.value));
									}}
									error={touched.number && Boolean(errors.number)}
									helperText={
										(touched.number && errors.number) ||
										i18n.t("contactModal.form.numberHelper")
									}
									placeholder="+55 (41) 99860-8485"
									variant="outlined"
									margin="dense"
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.address")}
										name="address"
										placeholder="Endereço completo"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.birthDate")}
										name="birthDate"
										type="date"
										fullWidth
										margin="dense"
										variant="outlined"
										InputLabelProps={{
											shrink: true,
										}}
									/>
								</div>
								<>
								<FormControlLabel
									label={i18n.t("contactModal.form.disableBot")}
									labelPlacement="start"
									control={
										<Switch
											size="small"
											checked={values.disableBot}
											onChange={() =>
                        setContact({ ...values, disableBot: !values.disableBot })
											}
											name="disableBot"
											color="primary"
										/>
									}
								/>
								</>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<Autocomplete
									multiple
									options={extraInfoTemplates}
									value={selectedTemplateOptions}
									onChange={(event, newValue) => setSelectedTemplateOptions(newValue)}
									getOptionLabel={option =>
										option.value ? `${option.name}: ${option.value}` : option.name
									}
									renderInput={params => (
										<TextField
											{...params}
											variant="outlined"
											margin="dense"
											label={i18n.t("contactModal.form.importExtraInfo")}
											placeholder={i18n.t("contactModal.form.importExtraInfoPlaceholder")}
										/>
									)}
								/>

								<div className={classes.extraAttr}>
									<Button
										style={{ flex: 1, marginTop: 8 }}
										variant="outlined"
										color="primary"
										disabled={selectedTemplateOptions.length === 0}
										onClick={() => {
											const mergedInfo = mergeExtraInfoTemplates(
												values.extraInfo || [],
												selectedTemplateOptions
											);
											setFieldValue("extraInfo", mergedInfo);
											setSelectedTemplateOptions([]);
										}}
									>
										{i18n.t("contactModal.buttons.importExtraInfo")}
									</Button>
								</div>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
